/**
 * Weekly challenges, Sketch Tales release gate, Draw Together turns, Share the Moment,
 * review-prompt gating and journal pagination.
 */
import {
  REVIEW_MIN_SAVED_DRAWINGS,
  STORY_MIN_DAYS_BETWEEN,
  WEEKLY_CHALLENGE_ANCHOR,
  WEEKLY_CHALLENGE_DAYS,
  WEEKLY_CHALLENGE_TARGET,
} from './constants';
import type { Drawing, StoryEpisode, WeeklyChallenge } from './content/model';
import type { ProgressState } from './progress';

/* ------------------------------ Weekly challenges ------------------------------ */

export interface ActiveChallenge {
  challenge: WeeklyChallenge;
  /** Occurrence key: challenge id + week index, so a re-run of the same theme starts empty. */
  key: string;
  startsAt: Date;
  endsAt: Date;
}

const DAY = 86400_000;

/** Non-overlapping weekly schedule anchored on a Monday, rotating through the challenge list. */
export function activeChallenge(challenges: WeeklyChallenge[], now: Date, anchor = WEEKLY_CHALLENGE_ANCHOR): ActiveChallenge | null {
  if (!challenges.length) return null;
  const a = new Date(anchor).getTime();
  const span = WEEKLY_CHALLENGE_DAYS * DAY;
  const week = Math.floor((now.getTime() - a) / span);
  if (week < 0) return null;
  const challenge = challenges[week % challenges.length]!;
  const startsAt = new Date(a + week * span);
  return { challenge, key: `${challenge.id}@${week}`, startsAt, endsAt: new Date(startsAt.getTime() + span) };
}

export function nextChallenge(challenges: WeeklyChallenge[], now: Date, anchor = WEEKLY_CHALLENGE_ANCHOR): ActiveChallenge | null {
  return activeChallenge(challenges, new Date(now.getTime() + WEEKLY_CHALLENGE_DAYS * DAY), anchor);
}

export const challengeQualifies = (c: WeeklyChallenge, d: Pick<Drawing, 'category' | 'subject'>) =>
  !!(c.qualifies.categories?.includes(d.category) || c.qualifies.subjects?.includes(d.subject));

export type ChallengeStatus = 'active' | 'completed' | 'claimed' | 'expired';

export function challengeStatus(p: ProgressState, a: ActiveChallenge, now: Date, target = WEEKLY_CHALLENGE_TARGET): ChallengeStatus {
  const w = p.weekly[a.key];
  if (w?.rewardClaimed) return 'claimed';
  if ((w?.entries.length ?? 0) >= target) return 'completed';
  return now >= a.endsAt ? 'expired' : 'active';
}

export function claimChallenge(p: ProgressState, key: string, now = new Date()): ProgressState {
  const w = p.weekly[key];
  if (!w || w.rewardClaimed) return p;
  return { ...p, weekly: { ...p.weekly, [key]: { ...w, rewardClaimed: true } }, updatedAt: now.toISOString() };
}

/** "3d 4h" style countdown parts. */
export function timeLeft(endsAt: Date, now: Date): { days: number; hours: number; minutes: number } {
  const ms = Math.max(0, endsAt.getTime() - now.getTime());
  return { days: Math.floor(ms / DAY), hours: Math.floor((ms % DAY) / 3600_000), minutes: Math.floor((ms % 3600_000) / 60_000) };
}

/* -------------------------------- Sketch Tales -------------------------------- */

/**
 * Episodes are released in order: the first on the unlock day, then one every
 * STORY_MIN_DAYS_BETWEEN days. Premium (signed-in) profiles see every released... all episodes.
 */
export function availableStories(stories: StoryEpisode[], unlockedAt: string | null, now: Date, premium: boolean): { available: StoryEpisode[]; nextAt: Date | null } {
  if (!unlockedAt) return { available: [], nextAt: null };
  if (premium) return { available: stories, nextAt: null };
  const start = new Date(unlockedAt).getTime();
  const span = STORY_MIN_DAYS_BETWEEN * DAY;
  const n = Math.min(stories.length, 1 + Math.max(0, Math.floor((now.getTime() - start) / span)));
  return { available: stories.slice(0, n), nextAt: n < stories.length ? new Date(start + n * span) : null };
}

/* -------------------------------- Draw Together -------------------------------- */

export interface DrawTogetherSession {
  hostId: string;
  partnerId: string;
  miniUnitId: string;
  /** Whose upload turn it is. */
  turn: string;
  uploads: { profileId: string; photoId: string | null }[];
}

export function startDrawTogether(hostId: string, partnerId: string, miniUnitId: string): DrawTogetherSession {
  if (!hostId || !partnerId || hostId === partnerId) throw new Error('draw_together_needs_two_profiles');
  return { hostId, partnerId, miniUnitId, turn: hostId, uploads: [] };
}

/** Uploads alternate turns: host first, then partner. */
export function recordTurn(s: DrawTogetherSession, profileId: string, photoId: string | null): DrawTogetherSession {
  if (profileId !== s.turn) throw new Error('not_your_turn');
  if (s.uploads.some((u) => u.profileId === profileId)) throw new Error('already_uploaded');
  const uploads = [...s.uploads, { profileId, photoId }];
  return { ...s, uploads, turn: profileId === s.hostId ? s.partnerId : s.hostId };
}

export const drawTogetherDone = (s: DrawTogetherSession) => s.uploads.length >= 2;

/* ------------------------------ Share the Moment ------------------------------ */

export type AwardType = 'heart' | 'rainbow' | 'star' | 'trophy' | 'special';
export const AWARD_TYPES: AwardType[] = ['heart', 'rainbow', 'star', 'trophy', 'special'];
export type Complimenter = 'mom' | 'dad' | 'sibling' | 'grandparent' | 'friend' | 'other';
export const COMPLIMENTERS: Complimenter[] = ['mom', 'dad', 'sibling', 'grandparent', 'friend', 'other'];

export interface SharedMoment {
  momentId: string;
  award: AwardType;
  complimenter: Complimenter;
  compliment: string;
  createdAt: string;
}

/* -------------------------------- Review prompt -------------------------------- */

/** At most once per app version, after enough saved drawings, and only when enabled. */
export function shouldPromptReview(opts: { enabled: boolean; appVersion: string; lastPromptedVersion: string | null; savedDrawings: number; force?: boolean }): boolean {
  if (opts.force) return true;
  if (!opts.enabled) return false;
  if (opts.lastPromptedVersion === opts.appVersion) return false;
  return opts.savedDrawings >= REVIEW_MIN_SAVED_DRAWINGS;
}

/* ----------------------------------- Journal ----------------------------------- */

export type JournalTheme = 'classic' | 'notebook' | 'gallery';
export const JOURNAL_THEMES: JournalTheme[] = ['classic', 'notebook', 'gallery'];
export type JournalLayout = 1 | 2 | 3;
/** Drawings per page for each layout. */
export const LAYOUT_CAPACITY: Record<JournalLayout, number> = { 1: 1, 2: 2, 3: 4 };

export function paginate<T>(items: T[], layout: JournalLayout): T[][] {
  const cap = LAYOUT_CAPACITY[layout];
  const pages: T[][] = [];
  for (let i = 0; i < items.length; i += cap) pages.push(items.slice(i, i + cap));
  return pages.length ? pages : [[]];
}

/** How the journal is shown: a paged book (themes x layouts) or one scrolling grid of every drawing. */
export type JournalMode = 'book' | 'grid';

/** A sticker placed on one journal drawing; x / y are relative to that drawing's card (0..1, may overhang a little). */
export interface ItemSticker {
  id: string;
  stickerId: string;
  x: number;
  y: number;
  rot: number;
  scale: number;
}

/** Old format: a sticker placed on a book page (moved when the layout or the filter changed). */
export interface PageSticker extends ItemSticker {
  page: number;
}

export const STICKER_OVERHANG = 0.2;
const clampRel = (v: number) => Math.min(1 + STICKER_OVERHANG, Math.max(-STICKER_OVERHANG, Math.round(v * 1000) / 1000));

/** Card cells of a book page in page coordinates (0..1), matching the landscape CSS grid. */
export function layoutCells(layout: JournalLayout): { x: number; y: number; w: number; h: number }[] {
  const n = LAYOUT_CAPACITY[layout];
  return Array.from({ length: n }, (_, i) => ({ x: i / n, y: 0, w: 1 / n, h: 1 }));
}

/**
 * Migrate page-indexed stickers to the drawing they were sitting on: rebuild the pages with the layout the
 * stickers were placed with, give each sticker to the nearest card on its page and convert its position
 * to card coordinates. Stickers on empty / missing pages are dropped.
 */
export function migratePageStickers<T extends { id: string }>(items: T[], layout: JournalLayout, stickers: PageSticker[]): Record<string, ItemSticker[]> {
  const pages = paginate(items, layout);
  const cells = layoutCells(layout);
  const out: Record<string, ItemSticker[]> = {};
  for (const s of stickers) {
    const page = pages[s.page];
    if (!page?.length) continue;
    let best = 0;
    let bestD = Infinity;
    page.forEach((_, i) => {
      const c = cells[i]!;
      const d = Math.hypot(c.x + c.w / 2 - s.x, c.y + c.h / 2 - s.y);
      if (d < bestD) {
        bestD = d;
        best = i;
      }
    });
    const c = cells[best]!;
    const item = page[best]!;
    (out[item.id] ??= []).push({ id: s.id, stickerId: s.stickerId, x: clampRel((s.x - c.x) / c.w), y: clampRel((s.y - c.y) / c.h), rot: s.rot, scale: s.scale });
  }
  return out;
}

/** Keep only well-formed stickers (synced metadata comes from the network). */
export function sanitizeItemStickers(raw: unknown, max = 12): ItemSticker[] {
  if (!Array.isArray(raw)) return [];
  const out: ItemSticker[] = [];
  for (const r of raw) {
    if (!r || typeof r !== 'object') continue;
    const s = r as Record<string, unknown>;
    const num = (v: unknown) => typeof v === 'number' && Number.isFinite(v);
    if (typeof s.id !== 'string' || typeof s.stickerId !== 'string' || !num(s.x) || !num(s.y) || !num(s.rot) || !num(s.scale)) continue;
    out.push({ id: s.id.slice(0, 40), stickerId: s.stickerId.slice(0, 40), x: clampRel(s.x as number), y: clampRel(s.y as number), rot: s.rot as number, scale: s.scale as number });
    if (out.length >= max) break;
  }
  return out;
}

export type Feeling = 1 | 2 | 3 | 4 | 5;
export const FEELINGS: Feeling[] = [1, 2, 3, 4, 5];
